import { GoogleTagManagerProvider } from "./GoogleTagManagerProvider";
import { VwoProvider } from "./VwoProvider";
import NetlifeConsentProvider from "./ConsentProviders/NetlifeConsentProvider";
import CookieBotProvider from "./ConsentProviders/CookieBotProvider";
import DummyConsentProvider from "./ConsentProviders/DummyConsentProvider";
class EpmsAnalytics {
    constructor(providers) {
        this.consentProvider = null;
        const portal = window.NTLF_CONFIG.portal;
        this.providers = [];
        for (const provider of providers) {
            if (provider === "google-tag-manager") {
                const gtm = new GoogleTagManagerProvider();
                gtm.setCurrency(portal.currency.code);
                this.providers.push(gtm);
                console.log("[Tracking] Added tracking provider", provider);
            }
            else if (provider === "vwo") {
                const vwo = new VwoProvider();
                vwo.setCurrency(portal.currency.code);
                this.providers.push(vwo);
                console.log("[Tracking] Added tracking provider", provider);
            }
        }
        // Make sure we can access 'this' in callback
        this.setAllowTracking = this.setAllowTracking.bind(this);
    }
    openConsentSettings() {
        // FIXME: Properly fix provider lookup. It's hard to know which
        //        consent provider is used, as GTM can have it built in
        // Cookiebot
        if (window.CookieConsent) {
            /** @ts-expect-error - This is not typed */
            window.CookieConsent.renew();
        }
    }
    onAddToCart(items) {
        this.trackServerProductOrderLineWithItems("onAddToCart", items);
    }
    onRemoveFromCart(items) {
        this.trackServerProductOrderLineWithItems("onRemoveFromCart", items);
    }
    // This method can be used in context where the call may both add and
    // remove content. Example: "Digital files" on desktop
    onCartChange(cartUpdate) {
        try {
            console.log(`[Tracking] onCartChange`, cartUpdate);
            for (const provider of this.consentedProviders()) {
                if ("onCartChange" in provider) {
                    provider.onCartChange(cartUpdate);
                }
            }
        }
        catch (err) {
            // We ignore all errors for now.
        }
    }
    onViewCart(items) {
        try {
            for (const provider of this.consentedProviders()) {
                if ("onViewCart" in provider) {
                    provider.onViewCart(items);
                }
            }
        }
        catch (err) {
            // We ignore all errors for now.
        }
    }
    onLogin(user) {
        try {
            for (const provider of this.consentedProviders()) {
                if ("onLogin" in provider && typeof provider.onLogin === "function") {
                    provider.onLogin(user);
                }
            }
        }
        catch (err) {
            // We ignore all errors for now.
        }
    }
    onRegisterUser(user) {
        try {
            for (const provider of this.consentedProviders()) {
                if ("onRegisterUser" in provider &&
                    typeof provider.onRegisterUser === "function") {
                    provider.onRegisterUser(user);
                }
            }
        }
        catch (err) {
            // We ignore all errors for now.
        }
    }
    onPageUser(user) {
        try {
            for (const provider of this.consentedProviders()) {
                if ("onPageUser" in provider &&
                    typeof provider.onPageUser === "function") {
                    provider.onPageUser(user);
                }
            }
        }
        catch (err) {
            // We ignore all errors for now.
        }
    }
    onBeginCheckout(items) {
        this.trackServerProductOrderLineWithItems("onBeginCheckout", items);
    }
    onPurchaseSuccessful(order) {
        try {
            for (const provider of this.consentedProviders()) {
                if ("onPurchaseSuccessful" in provider) {
                    provider.onPurchaseSuccessful(order);
                }
            }
        }
        catch (err) {
            // We ignore all errors for now.
            console.log("Purchase error", err);
        }
    }
    trackServerProductOrderLineWithItems(eventName, items) {
        console.log(`[Tracking] Order line with items for event ${eventName}`, items);
        try {
            for (const provider of this.consentedProviders()) {
                if (eventName in provider) {
                    provider[eventName](items);
                }
            }
        }
        catch (err) {
            // We ignore all errors for now.
            console.log("[Tracking] Error", err);
        }
    }
    /** The arms assigned to the subject being viewed, for segmenting events. */
    setExperimentArms(arms) {
        for (const provider of this.consentedProviders()) {
            if ("setExperimentArms" in provider &&
                typeof provider.setExperimentArms === "function") {
                provider.setExperimentArms(arms);
            }
        }
    }
    /**
     * A route change in a single-page shell, for the providers that would
     * otherwise only ever see the one real page load - see VwoProvider.
     */
    onVirtualPageView(url) {
        console.log("[Tracking] onVirtualPageView", url);
        for (const provider of this.consentedProviders()) {
            if ("onVirtualPageView" in provider &&
                typeof provider.onVirtualPageView === "function") {
                provider.onVirtualPageView(url);
            }
        }
    }
    /**
     * The providers allowed to receive events now. Ones that gate themselves are
     * exempt from our consent provider - docs/plans/drafts/consent-unification.md.
     */
    consentedProviders() {
        if (this.trackingAllowed()) {
            return this.providers;
        }
        return this.providers.filter((provider) => provider.handlesOwnConsent === true);
    }
    trackingAllowed() {
        var _a;
        const trackingAllowed = ((_a = this.consentProvider) === null || _a === void 0 ? void 0 : _a.getAllowTracking()) || false;
        console.log("[Tracking] Allowed?", trackingAllowed);
        return trackingAllowed;
    }
    setAllowTracking(value) {
        for (const provider of this.providers) {
            console.log("[Tracking] Setting allowed value for provider", value, provider);
            provider.setAllowTracking(value);
        }
    }
    setConsentProviderByString(providerId) {
        console.log("[Tracking] Setting consent provider id", providerId);
        if (providerId === "netlife") {
            this.consentProvider = new NetlifeConsentProvider(this.setAllowTracking);
        }
        else if (providerId === "cookiebot") {
            this.consentProvider = new CookieBotProvider(this.setAllowTracking);
        }
        else if (providerId === "dummy") {
            this.consentProvider = new DummyConsentProvider(this.setAllowTracking);
        }
        // Initialize the provider in case it need to run code to set tracking allowed
        if (this.consentProvider && !this.consentProvider.handleConsentPush()) {
            this.setAllowTracking(this.consentProvider.getAllowTracking());
        }
    }
    getConsentProvider() {
        return this.consentProvider;
    }
}
export default EpmsAnalytics;
// Make sure we can use the class in the global scope
// @ts-expect-error new EpmsAnalytics() is called in php
window.EpmsAnalytics = EpmsAnalytics;
